import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import type {
  CycleCountVarianceRow,
  ReturnNoteRegisterRow,
  StockAdjustmentRegisterRow,
  StockTransferRegisterRow,
} from '@/types/reports';
import { withId } from './reportHelpers';

const TABS = ['stockAdjustmentRegister', 'stockTransferRegister', 'cycleCountVariance', 'returnNoteRegister'] as const;

export default function AdjustmentsReportsPage() {
  const { t } = useTranslation('reports');
  const { formatDate } = useDateFormat();
  const { data: processConfiguration } = useProcessConfiguration();
  const showClientColumn = Boolean(processConfiguration?.thirdPartyLogisticsEnabled);
  const [tab, setTab] = useState(0);

  const { data: adjustments = [], isLoading: loadingAdjustments } = useQuery({
    queryKey: ['reports', 'stock-adjustment-register'],
    queryFn: async () => (await apiClient.get<StockAdjustmentRegisterRow[]>('/reports/stock-adjustment-register')).data,
    enabled: tab === 0,
  });
  const { data: transfers = [], isLoading: loadingTransfers } = useQuery({
    queryKey: ['reports', 'stock-transfer-register'],
    queryFn: async () => (await apiClient.get<StockTransferRegisterRow[]>('/reports/stock-transfer-register')).data,
    enabled: tab === 1,
  });
  const { data: cycleCounts = [], isLoading: loadingCycleCounts } = useQuery({
    queryKey: ['reports', 'cycle-count-variance'],
    queryFn: async () => (await apiClient.get<CycleCountVarianceRow[]>('/reports/cycle-count-variance')).data,
    enabled: tab === 2,
  });
  const { data: returnNotes = [], isLoading: loadingReturns } = useQuery({
    queryKey: ['reports', 'return-note-register'],
    queryFn: async () => (await apiClient.get<ReturnNoteRegisterRow[]>('/reports/return-note-register')).data,
    enabled: tab === 3,
  });

  const adjustmentColumns: DataTableColumn<ReturnType<typeof withId<StockAdjustmentRegisterRow>>[number]>[] = [
    { field: 'adjustmentNumber', headerName: t('columns.number') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'adjustmentDate', headerName: t('columns.date'), render: (r) => formatDate(r.adjustmentDate) },
    { field: 'reason', headerName: t('columns.reason'), filterable: true },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'currentQty', headerName: t('columns.currentQty') },
    { field: 'adjustedQty', headerName: t('columns.adjustedQty') },
    { field: 'variance', headerName: t('columns.variance') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<StockAdjustmentRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<StockAdjustmentRegisterRow>>[number]>]
      : []),
  ];

  const transferColumns: DataTableColumn<ReturnType<typeof withId<StockTransferRegisterRow>>[number]>[] = [
    { field: 'transferNumber', headerName: t('columns.number') },
    { field: 'fromWarehouseName', headerName: t('columns.fromWarehouse'), filterable: true },
    { field: 'toWarehouseName', headerName: t('columns.toWarehouse'), filterable: true },
    { field: 'transferDate', headerName: t('columns.date'), render: (r) => formatDate(r.transferDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'qty', headerName: t('columns.quantity') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<StockTransferRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<StockTransferRegisterRow>>[number]>]
      : []),
  ];

  const cycleCountColumns: DataTableColumn<ReturnType<typeof withId<CycleCountVarianceRow>>[number]>[] = [
    { field: 'cycleCountNumber', headerName: t('columns.number') },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'countDate', headerName: t('columns.date'), render: (r) => formatDate(r.countDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'locationCode', headerName: t('columns.location'), render: (r) => r.locationCode ?? '—' },
    { field: 'batchNo', headerName: t('columns.batch'), render: (r) => r.batchNo ?? '—' },
    { field: 'systemQty', headerName: t('columns.systemQty') },
    { field: 'countedQty', headerName: t('columns.countedQty'), render: (r) => r.countedQty ?? '—' },
    { field: 'variance', headerName: t('columns.variance'), render: (r) => r.variance ?? '—' },
    { field: 'accuracyPercent', headerName: t('columns.accuracyPercent'), render: (r) => (r.accuracyPercent != null ? `${r.accuracyPercent}%` : '—') },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<CycleCountVarianceRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<CycleCountVarianceRow>>[number]>]
      : []),
  ];

  const returnNoteColumns: DataTableColumn<ReturnType<typeof withId<ReturnNoteRegisterRow>>[number]>[] = [
    { field: 'returnNumber', headerName: t('columns.number') },
    { field: 'returnType', headerName: t('columns.returnType'), filterable: true },
    { field: 'partyName', headerName: t('columns.party'), render: (r) => r.partyName ?? '—', filterable: true },
    { field: 'warehouseName', headerName: t('columns.warehouse'), filterable: true },
    { field: 'returnDate', headerName: t('columns.date'), render: (r) => formatDate(r.returnDate) },
    { field: 'status', headerName: t('columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    { field: 'materialCode', headerName: t('columns.materialCode') },
    { field: 'materialName', headerName: t('columns.materialName') },
    { field: 'qty', headerName: t('columns.quantity') },
    { field: 'reason', headerName: t('columns.reason'), render: (r) => r.reason ?? '—' },
    ...(showClientColumn
      ? [{ field: 'clientName', headerName: t('columns.client'), render: (r: ReturnType<typeof withId<ReturnNoteRegisterRow>>[number]) => r.clientName ?? '—', filterable: true } as DataTableColumn<ReturnType<typeof withId<ReturnNoteRegisterRow>>[number]>]
      : []),
  ];

  return (
    <>
      <PageHeader title={t('modules.adjustments.title')} subtitle={t('modules.adjustments.subtitle')} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((key) => (
          <Tab key={key} label={t(`${key}.title`)} />
        ))}
      </Tabs>

      <Box hidden={tab !== 0}>
        <DataTable columns={adjustmentColumns} rows={withId(adjustments, (r) => `${r.adjustmentId}-${r.materialCode}`)} loading={loadingAdjustments} searchable exportFileName="stock-adjustment-register" />
      </Box>
      <Box hidden={tab !== 1}>
        <DataTable columns={transferColumns} rows={withId(transfers, (r) => `${r.transferId}-${r.materialCode}`)} loading={loadingTransfers} searchable exportFileName="stock-transfer-register" />
      </Box>
      <Box hidden={tab !== 2}>
        <DataTable columns={cycleCountColumns} rows={withId(cycleCounts, (r) => `${r.cycleCountId}-${r.materialCode}-${r.locationCode ?? ''}-${r.batchNo ?? ''}`)} loading={loadingCycleCounts} searchable exportFileName="cycle-count-variance" />
      </Box>
      <Box hidden={tab !== 3}>
        <DataTable columns={returnNoteColumns} rows={withId(returnNotes, (r) => `${r.returnNoteId}-${r.materialCode}`)} loading={loadingReturns} searchable exportFileName="return-note-register" />
      </Box>
    </>
  );
}
